import { useState, useCallback, useRef } from 'react'
import { apiGet, apiPost } from '../lib/api'
import { needsPolling } from '../lib/status'

type PaymentResult = {
    paymentId: string
    status: string
    amount?: number | string
    currency?: string
    clientSecret?: string
    captureId?: string
    invoiceUrl?: string
    proxyUrl?: string | null
}

export function usePayment() {
    const [creating, setCreating] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [capturing, setCapturing] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [polling, setPolling] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const abortRef = useRef(false)

    const createPayment = useCallback(async (body: any): Promise<PaymentResult> => {
        setCreating(true)
        setError(null)
        try {
            return await apiPost<PaymentResult>('/api/payments', body)
        } catch (err: any) {
            setError(err.message)
            throw err
        } finally {
            setCreating(false)
        }
    }, [])

    const confirmPayment = useCallback(async (paymentId: string, body: any): Promise<PaymentResult> => {
        setConfirming(true)
        setError(null)
        try {
            return await apiPost<PaymentResult>(`/api/payments/${paymentId}/confirm`, body)
        } catch (err: any) {
            setError(err.message)
            throw err
        } finally {
            setConfirming(false)
        }
    }, [])

    const capturePayment = useCallback(async (
        paymentId: string, amount: string, merchantInvoiceRef: string
    ): Promise<PaymentResult> => {
        setCapturing(true)
        setError(null)
        try {
            return await apiPost<PaymentResult>(`/api/payments/${paymentId}/capture`, {
                amount: parseFloat(amount),
                merchantInvoiceRef,
            })
        } catch (err: any) {
            setError(err.message)
            throw err
        } finally {
            setCapturing(false)
        }
    }, [])

    const cancelPayment = useCallback(async (paymentId: string): Promise<PaymentResult> => {
        setCancelling(true)
        setError(null)
        try {
            return await apiPost<PaymentResult>(`/api/payments/${paymentId}/cancel`, {})
        } catch (err: any) {
            setError(err.message)
            throw err
        } finally {
            setCancelling(false)
        }
    }, [])

    const getStatus = useCallback(async (paymentId: string): Promise<PaymentResult> => {
        return await apiGet<PaymentResult>(`/api/payments/${paymentId}`)
    }, [])

    const linkOrder = useCallback(async (
        paymentId: string, orderId: string, captureId?: string, invoiceUrl?: string,
    ): Promise<void> => {
        await apiPost(`/api/payments/${paymentId}/link-order`, { orderId, captureId, invoiceUrl })
    }, [])

    const getInvoiceUrl = useCallback(async (paymentId: string): Promise<{ invoiceUrl: string | null; proxyUrl: string | null }> => {
        return await apiGet<{ invoiceUrl: string | null; proxyUrl: string | null }>(`/api/payments/${paymentId}/invoice-url`)
    }, [])

    const getLinkedOrder = useCallback(async (
        paymentId: string,
    ): Promise<{ orderId: string | null; orderName: string | null }> => {
        return await apiGet<{ orderId: string | null; orderName: string | null }>(`/api/payments/${paymentId}/order`)
    }, [])

    const stopPolling = useCallback(() => {
        abortRef.current = true
    }, [])

    const pollUntilResolved = useCallback(async (paymentId: string): Promise<PaymentResult | null> => {
        abortRef.current = false
        setPolling(true)
        const intervals = [2000, 4000, 8000, 15000, 30000]
        let attempt = 0
        const maxAttempts = 20

        try {
            while (attempt < maxAttempts && !abortRef.current) {
                const delay = intervals[Math.min(attempt, intervals.length - 1)]
                await new Promise(r => setTimeout(r, delay))
                if (abortRef.current) return null

                const result = await getStatus(paymentId)
                if (!needsPolling(result.status)) return result
                attempt++
            }
            return null
        } finally {
            setPolling(false)
        }
    }, [getStatus])

    return {
        creating, confirming, capturing, cancelling, polling, error,
        createPayment, confirmPayment, capturePayment, cancelPayment,
        getStatus, linkOrder, getInvoiceUrl, getLinkedOrder, pollUntilResolved, stopPolling,
    }
}
